import { useMemo } from 'react';

import {
  FILEPATH_PERMALINK_MATCHER,
  LINE_PERMALINK_MATCHER,
  SIDE_BY_SIDE_LINE_PERMALINK_MATCHER,
  getPermalink,
  getDiffRange,
} from '@atlassian/bitkit-diff/exports/diff-permalink';

import { useParams } from 'src/router/hooks';
import urls from 'src/urls';

import { TabSelectedType } from '../pull-request-tabbed';

import { useSelectedTab } from './use-selected-tab';

/**
 * redirectLink: used for updating the route
 *
 * newTab: used for updating the selected tab
 */
type TabRedirect = {
  redirectLink: string;
  newTab: TabSelectedType;
};

/**
 * Returns a TabRedirect object if a redirect is necessary based on the current URL and selected tab.
 *
 * @return {TabRedirect | undefined} The redirect object if a redirect is necessary, otherwise undefined.
 */
export const useTabRedirect = (
  currentTab: TabSelectedType
): TabRedirect | undefined => {
  const permalink = getPermalink() || '';
  const selectedTab = useSelectedTab();
  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();
  const diffRange = getDiffRange();

  /**
   * Builds a redirect object for the specified tab with the given permalink.
   *
   * @param {TabSelectedType} tab - The selected tab.
   * @return {TabRedirect} The redirect object.
   */
  const buildRedirect = useMemo(
    () =>
      (tab: TabSelectedType): TabRedirect => {
        const permalinkSection = permalink ? `#${permalink}` : '';
        return {
          redirectLink: `${urls.ui.pullRequestTab(
            repositoryOwner,
            repositorySlug,
            pullRequestId,
            tab
          )}${diffRange || ''}${permalinkSection}`,
          newTab: tab,
        };
      },
    [permalink, diffRange, repositoryOwner, repositorySlug, pullRequestId]
  );

  if (!permalink && !diffRange) {
    return undefined;
  }

  // If permalink is a chg file or line, or if a diffRange exists, redirect to diff tab
  if (
    selectedTab !== 'diff' &&
    (LINE_PERMALINK_MATCHER.test(permalink) ||
      FILEPATH_PERMALINK_MATCHER.test(permalink) ||
      SIDE_BY_SIDE_LINE_PERMALINK_MATCHER.test(permalink) ||
      diffRange)
  ) {
    return buildRedirect('diff');
    // If permalink is redirecting from the overview tab to the diff tab
  } else if (selectedTab === 'diff' && currentTab !== 'diff') {
    return buildRedirect('diff');
  }
  return undefined;
};
